import {
  BEYAN_ADI,
  DOSYA_HAT_ADI,
  DOSYA_TUR_ADI,
  KALEM_DURUMU_ADI,
  belgeCipi,
  pkgNo,
  satirAcilisi,
  satirEkranAdi,
  type BelgeKutugu,
  type DogmazNedeni,
  type DosyaBolumu,
  type DosyaSatiri,
  type DosyaSayaci,
  type DosyaTuru,
  type IsBlogu,
  type IzlenebilirlikSatiri,
  type IzlenebilirlikTakilan,
  type KalemDurumu,
  type KalemGrubu,
  type KalemNo,
  type KayitHedefi,
  type KutukBagi,
  type ListeKalemi,
  type OemKaydi,
  type ParcaDurumu,
  type RiserDosyasiYaniti,
  type RiserOzeti,
  type SayfaAraligi,
  type SimdiTakili,
  type Yuva,
} from "@oasis/domain";
import { cevir } from "@/lib/dil";

/**
 * D-058: Riser Documentation sayfasının saf yardımcıları. Sunucu hazır cümle göndermez (tür anahtarı, kapsam, eksik yuvalar, sayılar);
 * ekrandaki bütün cümleler burada `cevir` ile kurulur. Bölüm ve kalem adları Oil States şablonundaki adlardır, çevrilmez; belge türü,
 * durum ve ekran adları alan paketinde İngilizce sabittir, burada `cevir(degisken)` ile gösterilir (Türkçeleri tr-elle.tsv'de).
 * JSX yok; tarih ve sayfa biçimi dışarıdan verilir (`Bicim`): modül React ve oturum katmanını yüklemez.
 *
 * Cümleler `cevir("… {0} …", deger)` ile kurulur. Etiket + değer çiftlerinde ("Weld W1", "welder K2", "Stock package PKG-0002") etiket
 * ayrı anahtardır: kısa önekli bir kalıp ("welder {0}") sözlüğe girerse `cevir`in kalıp eşlemesi sunucudan gelen başka metinleri
 * ("welder Identity empty …") yarım çevirir (lib/dil.ts, KALIP EŞLEME).
 */

export type Ton = "neutral" | "info" | "ok" | "warn" | "bad";

/** Tarih ("25/09/2026") ve sayfa aralığı ("p. 8–9") biçimi; ekran `tarih` ve `sayfaEtiketi` verir. */
export interface Bicim {
  tarih: (iso: string) => string;
  sayfa: (a: SayfaAraligi) => string;
}

/* ---------------------------------------------------------------- riser seçici ---------------------------------------------------------------- */

const kucuk = (s: string | null | undefined) => (s ?? "").toLocaleLowerCase("tr");

/** Riser seçici: arama RN ve Assembly SN içinde gezer; `batchNo` null ise bütün batch'ler ("All"). Sıra sunucudan geldiği gibi (RN). */
export function riserSuz<T extends Pick<RiserOzeti, "rn" | "batchNo" | "assemblySn">>(riserler: readonly T[], arama: string, batchNo: string | null): T[] {
  const q = kucuk(arama.trim());
  return riserler.filter((r) => (batchNo === null || r.batchNo === batchNo) && (!q || kucuk(r.rn).includes(q) || kucuk(r.assemblySn).includes(q)));
}

/** Riserin dosyasının ve Excel listesinin API yolu. */
export const dosyaYolu = (rn: string) => `/riser-belgeleri/${encodeURIComponent(rn)}`;
export const listeYolu = (rn: string) => `${dosyaYolu(rn)}/liste.xlsx`;

/* ---------------------------------------------------------------- tonlar ve sınıflar ---------------------------------------------------------------- */

/** Liste durumunun çip tonu (Tailwind sınıfı dinamik kurulmaz; ton `Etiket`e verilir). */
export const DURUM_TONU: Record<KalemDurumu, Ton> = { dahil: "ok", beyan: "warn", sonda: "info", eksik: "bad", dogmaz: "neutral" };
/** Parça durumunun çip tonu; "bilinmiyor" çip değil tiredir. */
export const PARCA_TONU: Record<Exclude<ParcaDurumu["tur"], "bilinmiyor">, Ton> = { orijinal: "ok", degisti: "info", tamir: "warn", koken: "warn" };

/** Başlıktaki hat karesi: Finishing satırındaki hat durum kodunun rengi (Planlama.tsx `kodRenk` ile aynı eşleme; D-067: "islemde" taralı). */
export const HAT_KARESI: Readonly<Record<string, string>> = { hazir: "bg-ok", islemde: "bg-info-tarali", stok: "bg-warn", malzeme: "bg-bad" };
export const hatKaresi = (kod: string) => HAT_KARESI[kod] ?? "bg-surface-3";

/* ---------------------------------------------------------------- küçük parçalar ---------------------------------------------------------------- */

/** Hat adı: "Main" → "Main Tube"; beş hattın dışındaki değer ("C/K 1") olduğu gibi. */
export const hatAdi = (hat: string | null | undefined): string => (hat ? ((DOSYA_HAT_ADI as Readonly<Record<string, string>>)[hat] ?? hat) : "");
/** "W1, W2" */
export const kaynakNolari = (nolar: readonly number[]) => nolar.map((n) => `W${n}`).join(", ");
/** İşin adı: "Job 05d8b642" (kimliğin kısa biçimi öteki ekranlardakiyle aynı: ilk sekiz karakter). */
const isAdi = (id: string) => `${cevir("Job")} ${id.slice(0, 8)}`;
/**
 * D-062: NDT inspektörü, adıyla: "technician Deniz Örnek" (bu ekranda terim "technician" kalır). Ad gelmediyse yalnız "technician";
 * inspektörün sistem kimliği hiçbir metne yazılmaz.
 */
const teknisyen = (ad: string | null | undefined) => [cevir("technician"), ad?.trim()].filter(Boolean).join(" ");
const yer = (hat: string | null | undefined, nolar: readonly number[]) => [hatAdi(hat), kaynakNolari(nolar)].filter(Boolean).join(" ");
/** Belge türü adı; "{0}" ara değeri verici riserdir. */
const turAdi = (tur: DosyaTuru, rn?: string | null): string => {
  const ad = DOSYA_TUR_ADI[tur];
  return ad.includes("{0}") ? cevir(ad, rn ?? "") : cevir(ad);
};
const beyanAdlari = (s: Pick<DosyaSatiri, "beyanlar">) => s.beyanlar.map((b) => cevir(BEYAN_ADI[b]));
/** OEM kitabının sayfa aralığı olarak gösterilen belgeler (ayrı dosya değil). */
const MRB_SAYFA_TURLERI: readonly DosyaTuru[] = ["dnv_urun_sertifikasi", "dvr_oem"];
const mrbSayfasiMi = (s: Pick<DosyaSatiri, "tur" | "kaynak">) => s.kaynak === "mrb" && MRB_SAYFA_TURLERI.includes(s.tur);

/** "1 page" / "73 pages" */
export const sayfaAdedi = (n: number) => (n === 1 ? cevir("1 page") : cevir("{0} pages", n));
/** "1 document" / "3 documents"; kalem sayısı verilirse "· 5 list items". */
export const belgeSayaci = (belge: number, kalem?: number): string => {
  const b = belge === 1 ? cevir("1 document") : cevir("{0} documents", belge);
  return kalem === undefined ? b : `${b} · ${cevir("{0} list items", kalem)}`;
};
/** Bölüm kartının sağ üstü: "1 document · 5 list items"; Bölüm 4'te "11 documents · by line, job and weld". */
export const bolumSayaci = (b: Pick<DosyaBolumu, "no" | "belgeSayisi" | "kalemSayisi">): string =>
  b.no === "4" ? `${belgeSayaci(b.belgeSayisi)} · ${cevir("by line, job and weld")}` : belgeSayaci(b.belgeSayisi, b.kalemSayisi);
/** Riser başlığının sağı: "31 documents on file · 21 of 30 list items covered". */
export const ozetMetni = (belgeSayisi: number, s: Pick<DosyaSayaci, "kapsanan" | "toplam">): string =>
  `${belgeSayisi === 1 ? cevir("1 document on file") : cevir("{0} documents on file", belgeSayisi)} · ${cevir("{0} of {1} list items covered", s.kapsanan, s.toplam)}`;
/** Liste kartının sağ üstü: "30 list items · 21 covered · 6 missing · 3 not applicable". */
export const listeOzeti = (s: DosyaSayaci): string => cevir("{0} list items · {1} covered · {2} missing · {3} not applicable", s.toplam, s.kapsanan, s.eksik, s.dogmaz);
export const durumAdi = (d: KalemDurumu): string => cevir(KALEM_DURUMU_ADI[d]);

/* ---------------------------------------------------------------- yanıtın dizini ---------------------------------------------------------------- */

/** Satır kimliği → satır ve iş → Bölüm 4 bloğu (eksik yuvaların ve `ilgili` bağlarının adını yazmak için). */
export interface Dizin {
  satir: Map<string, DosyaSatiri>;
  blok: Map<string, IsBlogu>;
}
export const BOS_DIZIN: Dizin = { satir: new Map(), blok: new Map() };

export function dosyaDizini(v: Pick<RiserDosyasiYaniti, "bolumler" | "referans" | "liste">): Dizin {
  const satir = new Map<string, DosyaSatiri>();
  const blok = new Map<string, IsBlogu>();
  const ekle = (liste: readonly DosyaSatiri[]) => liste.forEach((s) => satir.set(s.id, s));
  for (const b of v.bolumler) {
    b.gruplar.forEach((g) => ekle(g.satirlar));
    b.bloklar.forEach((x) => {
      blok.set(x.isId, x);
      ekle(x.satirlar);
    });
    ekle(b.kutukSatirlari);
  }
  ekle(v.referans);
  v.liste.forEach((k) => {
    ekle(k.satirlar);
    ekle(k.beyanlar);
  });
  return { satir, blok };
}

/** Kapsam açıklamasındaki "From riser {0}" çipi için bu riserin ilk verici riseri; yoksa null. */
export function ornekVericiRn(v: Pick<RiserDosyasiYaniti, "izlenebilirlik">): string | null {
  for (const h of v.izlenebilirlik) {
    if (h.kokenRn) return h.kokenRn;
    for (const p of [h.box, h.pipe, h.pin]) if (p.tur === "koken") return p.rn;
  }
  return null;
}

/* ---------------------------------------------------------------- belge satırı ---------------------------------------------------------------- */

function kutukCipi(k: BelgeKutugu | null): string {
  switch (k) {
    case "wps": return cevir("Register: WPS");
    case "kaynakci": return cevir("Register: Welder");
    case "ndt_inspektor": return cevir("Register: NDT inspector");
    case "sarf": return cevir("Register: Lot");
    case "alet": return cevir("Register: Instrument");
    case "stok_paket": return cevir("Register: Stock");
    default: return cevir("Registers");
  }
}

/** Kapsam çipi: "Riser" · "Line: Choke" · "Job" · "Weld W1" · "Part" · "Register: WPS" · "Work order" · "Project". */
export function cipMetni(s: Pick<DosyaSatiri, "kapsam" | "hat" | "kaynakNolar" | "kutuk">): string {
  switch (s.kapsam) {
    case "riser": return cevir("Riser");
    case "hat": return s.hat ? `${cevir("Line")}: ${hatAdi(s.hat)}` : cevir("Line");
    case "is": return cevir("Job");
    case "kaynak": return s.kaynakNolar.length ? `${cevir("Weld")} ${kaynakNolari(s.kaynakNolar)}` : cevir("Weld");
    case "parca": return cevir("Part");
    case "kutuk": return kutukCipi(s.kutuk?.kutuk ?? null);
    case "is_emri": return cevir("Work order");
    case "proje": return cevir("Project");
  }
}

export interface Cip {
  ton: Ton;
  metin: string;
}

/** Satırın çipleri: önce kapsam, sonra "via Register", "From riser {0}", "also covers {0}", "cancelled job", "not in the DNV file". */
export function satirCipleri(s: Pick<DosyaSatiri, "kapsam" | "hat" | "kaynakNolar" | "kutuk" | "kokenRn" | "ortakRnler" | "iptalIs" | "tur">): Cip[] {
  const c: Cip[] = [{ ton: "neutral", metin: cipMetni(s) }];
  if (s.kutuk) c.push({ ton: "info", metin: cevir("via Register") });
  if (s.kokenRn) c.push({ ton: "warn", metin: cevir("From riser {0}", s.kokenRn) });
  if (s.ortakRnler.length) c.push({ ton: "neutral", metin: cevir("also covers {0}", s.ortakRnler.join(", ")) });
  if (s.iptalIs) c.push({ ton: "neutral", metin: cevir("cancelled job") });
  if (s.tur === "foto_calisma") c.push({ ton: "neutral", metin: cevir("not in the DNV file") });
  return c;
}

/** Satırın adı (bağlantı metni): belge türü adı; fit-up kaydında kaynak numaralarıyla ("Fit-up record W1"). */
export function satirAdi(s: Pick<DosyaSatiri, "tur" | "kokenRn" | "kayit" | "kaynakNolar">): string {
  const ad = turAdi(s.tur, s.kokenRn);
  return s.kayit === "fitup" && s.kaynakNolar.length ? `${ad} ${kaynakNolari(s.kaynakNolar)}` : ad;
}

/**
 * Kütük kaydının kodu: "Stock package PKG-0002" · "W-00313 rev 2" · "welder K2" · "lot 1234" · "instrument A-12".
 * D-062: NDT inspektörünün kodu yoktur; adıyla yazılır ("technician Deniz Örnek"). `kod` alanındaki sistem kimliği yazılmaz.
 */
export function kutukKodu(k: Pick<KutukBagi, "kutuk" | "kod" | "rev" | "ad">): string {
  switch (k.kutuk) {
    case "stok_paket": return `${cevir("Stock package")} ${k.kod}`;
    case "wps": return k.rev ? cevir("{0} rev {1}", k.kod, k.rev) : k.kod;
    case "kaynakci": return `${cevir("welder")} ${k.kod}`;
    case "ndt_inspektor": return teknisyen(k.ad);
    case "sarf": return `${cevir("lot")} ${k.kod}`;
    case "alet": return `${cevir("instrument")} ${k.kod}`;
  }
}

/**
 * Kütük kaydının kullanıldığı yer: "used in Main Tube W1, W2 · Choke W1"; alette fit-up ya da basınç testi sertifikası.
 * "used in" ayrı anahtardır: "used in {0}" kalıbı sözlükte Registers'ın kullanım sayacıdır ("used in 3" → "3 yerde kullanıldı").
 * D-062: NDT sertifikasında inspektörün imzaladığı raporlar ("signed: NDT report after welding Main Tube"); rapor yoksa yazılmaz.
 */
function kullanimMetni(s: Pick<DosyaSatiri, "tur" | "kutuk" | "ilgili">, d: Dizin): string | null {
  if (s.tur === "kalibrasyon_test") {
    const hatlar = [...new Set(s.ilgili.map((id) => hatAdi(d.satir.get(id)?.hat)).filter(Boolean))];
    const ad = cevir("named on the Pressure Test Certificate");
    return hatlar.length ? `${ad}: ${hatlar.join(", ")}` : ad;
  }
  if (s.tur === "ndt_yeterlilik") {
    const raporlar = s.ilgili.flatMap((id) => {
      const r = d.satir.get(id);
      return r ? [[satirAdi(r), hatAdi(r.hat)].filter(Boolean).join(" ")] : [];
    });
    return raporlar.length ? cevir("signed: {0}", raporlar.join(", ")) : null;
  }
  const yerler = (s.kutuk?.kullanim ?? []).map((y) => yer(y.hat, y.kaynakNolar)).filter(Boolean);
  if (!yerler.length) return null;
  return s.tur === "kalibrasyon_fitup" ? cevir("used in {0} fit-up", yerler.join(" · ")) : `${cevir("used in")} ${yerler.join(" · ")}`;
}

/**
 * Satırın alt metni (parçalar " · " ile): dosya adı ve sayfa ("… .pdf · 73 pages", "OEM MRB · p. 8–9"); kütük belgesinde kayıt kodu,
 * belgenin kendi türü ve kullanıldığı yer; fit-up kaydında "digital form · released …"; beyanda beyanın adı, yeri, kim ve ne zaman.
 * D-072: beyan edilen şey (muayene bekleyen kalem: "Fastener #48 Bolt (Locking Bolt) ×6") adımın adından sonra, ham.
 * D-062: 5.4'ün satırı Registers › NDT inspectors'tan gelen kütük belgesidir: "technician Deniz Örnek · NDT certificate · signed: …"
 * (sertifikanın dosya adı ve inspektörün sistem kimliği yazılmaz).
 * `hatGoster`: iş ve kaynak kapsamlı satırda hat adı da yazılır (Bölüm 4 bloklarının dışında).
 * D-071 (Dashboard'daki riser ayrıntısı; ikisi de verilmezse metin eskisi gibidir): `tarihGizli` ile beyanın günü yazılmaz ve fit-up
 * kaydında "released" sözü günsüz kalır; `beyanEdenGizli` ile beyanı yapanın adı yazılmaz.
 */
export function satirAltMetni(s: DosyaSatiri, b: Bicim, o: { hatGoster?: boolean; dizin?: Dizin; tarihGizli?: boolean; beyanEdenGizli?: boolean } = {}): string {
  const p: string[] = [];
  const isSatiri = s.kapsam === "is" || s.kapsam === "kaynak";
  if (s.kayit === "beyan") {
    p.push(...beyanAdlari(s));
    if (s.adimAd) p.push(cevir(s.adimAd));
    // D-072: beyan edilen şey ("Fastener #48 Bolt (Locking Bolt) ×6"), veri olarak ham.
    if (s.beyanKonusu) p.push(s.beyanKonusu);
    const y = yer(s.hat, s.kaynakNolar);
    if (y) p.push(y);
    if (s.yukleyen && !o.beyanEdenGizli) p.push(s.yukleyen);
    if (s.ts && !o.tarihGizli) p.push(b.tarih(s.ts));
    return p.join(" · ");
  }
  if (s.kayit === "fitup") {
    p.push(cevir("digital form"));
    if (s.ts) p.push(o.tarihGizli ? cevir("released") : `${cevir("released")} ${b.tarih(s.ts)}`);
    p.push(...beyanAdlari(s));
    if (o.hatGoster && s.hat) p.push(hatAdi(s.hat));
    return p.join(" · ");
  }
  if (s.adimAd) p.push(cevir(s.adimAd));
  if (o.hatGoster && isSatiri && s.hat) p.push(hatAdi(s.hat));
  if (s.kutuk) {
    p.push(kutukKodu(s.kutuk));
    if (s.kutuk.iz) p.push(s.kutuk.iz);
    if (s.kutuk.poNo) p.push(cevir("PO {0}", s.kutuk.poNo));
    p.push(belgeCipi({ tur: s.kutuk.belgeTuru, belgeNo: s.kutuk.belgeNo, tarih: null, poNo: null, sayfaBas: s.sayfa?.sayfaBas ?? null, sayfaSon: s.sayfa?.sayfaSon ?? null }, { cevir: (m) => cevir(m) }));
    if (s.dosyaAd) p.push(s.dosyaAd);
    if (!s.sayfa && s.sayfaSayisi) p.push(sayfaAdedi(s.sayfaSayisi));
  } else {
    if (s.tur === "mrb" || mrbSayfasiMi(s)) p.push(cevir("OEM MRB"));
    else if (s.dosyaAd) p.push(s.dosyaAd);
    if (s.sayfa) p.push(b.sayfa(s.sayfa));
    else if (s.sayfaSayisi) p.push(sayfaAdedi(s.sayfaSayisi));
  }
  if (s.tur === "mrb" && s.dizinSatiri) p.push(cevir("document index {0} rows", s.dizinSatiri));
  if (s.sertlik) p.push(cevir("three hardness values per weld"));
  if (s.kutuk) {
    const k = kullanimMetni(s, o.dizin ?? BOS_DIZIN);
    if (k) p.push(k);
  }
  return p.join(" · ");
}

/** Satırın kaynak ekranı: "Field list"; başka riserin ekranıysa "Field list (R120)"; tanınmayan değerde boş. */
export function satirEkrani(s: Pick<DosyaSatiri, "kaynak" | "kutuk" | "kaynakRn">): string {
  const ad = satirEkranAdi(s);
  if (!ad) return "";
  return s.kaynakRn ? `${cevir(ad)} (${s.kaynakRn})` : cevir(ad);
}

/**
 * Son kolon: yükleyen ve tarih. OEM kitabının sayfa aralığında "from the OEM record book"; fit-up kaydında yalnız release veren; beyanda boş (alt metinde).
 * D-071: `yukleyenGizli` (Dashboard'daki riser ayrıntısı) ile hücre boş kalır: yükleyen, release veren ve yükleme günü yazılmaz. OEM
 * kitabının sözü yükleyen değildir, kalır. Seçenek verilmezse sonuç eskisi gibidir.
 */
export function satirSonKolon(s: Pick<DosyaSatiri, "kayit" | "tur" | "kaynak" | "yukleyen" | "ts">, b: Bicim, o: { yukleyenGizli?: boolean } = {}): string {
  if (s.kayit === "beyan") return "";
  if (s.kayit === "fitup") return o.yukleyenGizli ? "" : (s.yukleyen ?? "");
  if (mrbSayfasiMi(s)) return cevir("from the OEM record book");
  return o.yukleyenGizli ? "" : [s.yukleyen, s.ts ? b.tarih(s.ts) : null].filter(Boolean).join(" · ");
}

/* ---------------------------------------------------------------- tıklama ---------------------------------------------------------------- */

/** Dijital kaydın ya da beyanın uygulama içi rotası. */
export function kayitYolu(h: KayitHedefi): string {
  const p = encodeURIComponent;
  switch (h.ekran) {
    case "fitup": return h.weldId ? `/fitup/${p(h.tamirId)}/${p(h.weldId)}` : `/fitup/${p(h.tamirId)}`;
    case "komponent": return `/komponent/${p(h.tamirId)}`;
    case "weldmap": return `/weldmap/${p(h.tamirId)}`;
    case "assembly": return "/assembly";
    case "teslim": return "/teslim";
    // D-072: muayene bekleyen kalemdeki "No record available" beyanı; riserin triyaj sayfası (`rn` muayene setinin numarası).
    case "muayene": return `/dogrulama/${p(h.rn)}`;
  }
}

export type Acilis =
  | { tip: "rota"; yol: string }
  | { tip: "goruntuleyici"; dokumanId: string; sayfa: number }
  | { tip: "sekme"; dokumanId: string }
  | { tip: "yok" };

/**
 * Satır tıklanınca ne açılır (karar alan paketinde: `satirAcilisi`): kayıt ve beyan uygulama içi rotaya; OEM kitabının sayfa aralıklı
 * PDF satırı uygulama içi sayfa görüntüleyiciye; öteki belgeler (PDF, resim, excel) yeni sekmeye; belge kimliği yoksa bağlantı yok.
 */
export function acilis(s: Pick<DosyaSatiri, "hedef" | "dokumanId" | "kaynak" | "pdf" | "sayfa">): Acilis {
  const a = satirAcilisi(s);
  if (a.tip === "rota") return { tip: "rota", yol: kayitYolu(a.hedef) };
  if (a.tip === "sekme") return { tip: "sekme", dokumanId: a.dokumanId };
  return a;
}

/* ---------------------------------------------------------------- "Documents on file" ---------------------------------------------------------------- */

/** Belgesi olmayan kalemin satırı: "No document." ve kalemde ne beklendiği. */
export function bosAciklama(no: KalemNo): string {
  switch (no) {
    case "1.1": return `${cevir("No document yet.")} ${cevir("Issued by Oil States when the overhaul is complete; one page for this riser.")}`;
    case "1.5": return `${cevir("No document.")} ${cevir("Identification plate photos are expected here.")}`;
    case "3.1": return `${cevir("No document.")} ${cevir("The material certification list (OSIUK-Q-FM-0024) of the parts bought for this riser comes here.")}`;
    case "5.4": return `${cevir("No document.")} ${cevir("The qualification record of the technician who signed the NDT reports comes here.")}`;
    default: return cevir("No document.");
  }
}

/** Kalem grubunun başlığı ("Design Approval · Certificate / Report"); kalem adları çevrilmez. */
export const grupBasligi = (g: Pick<KalemGrubu, "kalemler">): string => g.kalemler.map((k) => k.ad).join(" · ");

/** Grup başlığının yanındaki not. */
export function grupNotu(g: Pick<KalemGrubu, "not">): string {
  if (g.not === "imzali_dosya_sirasi") return cevir("order as in the signed files");
  if (g.not === "bolum_4") return cevir("shown with the welds in section 4");
  return "";
}

/**
 * Grubun belge satırı dışındaki satırları: `bos` belge yokken ("No document. …"), `ek` belgelerden sonra her zaman ya da kalem boşken
 * çizilen not (2.2: DNV Survey Report henüz düzenlenmedi; 3.5: ana boruda basınç testi yok). 5.1–5.3 grubunda satır çizilmez.
 */
export function grupBosMetni(g: Pick<KalemGrubu, "kalemler" | "not" | "satirlar">): { bos: string | null; ek: string | null } {
  if (g.not === "bolum_4") return { bos: null, ek: null };
  const var_ = (no: KalemNo) => g.satirlar.some((s) => s.kalem === no);
  const ilk = g.kalemler[0]?.no;
  if (ilk === "2.1") {
    return {
      bos: var_("2.1") ? null : cevir("No document."),
      ek: var_("2.2") ? null : cevir("DNV Survey Report: not issued yet. Added by DNV after the file is reviewed."),
    };
  }
  if (ilk === "3.5") return { bos: g.satirlar.length ? null : cevir("No document."), ek: cevir("Main Tube: no pressure test on the main tube.") };
  return { bos: g.satirlar.length || !ilk ? null : bosAciklama(ilk), ek: null };
}

/** Bölüm 4 blok başlığı: "4.1 · Main Tube · Job 05d8b642 · ST-03 Pipe cut + Replacement · W1 Box to pipe butt, W2 Pin to pipe butt". */
export function blokBasligi(b: Pick<IsBlogu, "sira" | "isId" | "hat" | "poz" | "is" | "kaynaklar">): string {
  return [
    `4.${b.sira}`,
    hatAdi(b.hat),
    isAdi(b.isId),
    [b.poz, b.is ? cevir(b.is) : ""].filter(Boolean).join(" "),
    b.kaynaklar.map((w) => [`W${w.no}`, w.ek].filter(Boolean).join(" ")).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
}

/* ---------------------------------------------------------------- izlenebilirlik tablosu ---------------------------------------------------------------- */

/** Box, pipe ya da pin hücresi: "original" · "replaced · PKG-0002" · "repaired · W1" · "From riser R120"; bilinmiyorsa tire. */
export function parcaMetni(p: ParcaDurumu): string {
  switch (p.tur) {
    case "koken": return cevir("From riser {0}", p.rn);
    case "degisti": return p.paketNolar.length ? `${cevir("replaced")} · ${p.paketNolar.map(pkgNo).join(", ")}` : cevir("replaced");
    case "tamir": return p.kaynakNolar.length ? `${cevir("repaired")} · ${kaynakNolari(p.kaynakNolar)}` : cevir("repaired");
    case "orijinal": return cevir("original");
    case "bilinmiyor": return "—";
  }
}
export const parcaTonu = (p: ParcaDurumu): Ton | null => (p.tur === "bilinmiyor" ? null : PARCA_TONU[p.tur]);

/** "Line SN (old ID → new ID)" hücresi: "<eski> → unchanged" · "<eski> → <yeni>" · "from riser R120"; kayıt yoksa tire. */
export function snMetni(s: Pick<IzlenebilirlikSatiri, "snEski" | "snYeni" | "snDegismedi" | "kokenRn">): string {
  const p: string[] = [];
  const yeni = s.snEski && s.snDegismedi ? cevir("unchanged") : s.snYeni;
  if (s.snEski) p.push(yeni ? `${s.snEski} → ${yeni}` : s.snEski);
  else if (yeni) p.push(`— → ${yeni}`);
  if (s.kokenRn) p.push(cevir("from riser {0}", s.kokenRn));
  return p.join(" · ") || "—";
}

/**
 * "OEM record" hücresi: "OEM MRB · section 1 · p. 31–96" · "OEM MRB of R120 · section 2A / 2B · p. 97–161". Sayfa hesaplanamayan
 * kitapta bölüm de yazılmaz: hücre yalnız kitabın adıdır ("OEM MRB").
 * D-059: Choke ya da Kill için Project documents'ta elle aralık girildiyse sunucu bölümleri boş gönderir (hangisinin 2A, hangisinin
 * 2B olduğu bilinmez): hücre yalnız hattın kendi aralığıdır ("OEM MRB · p. 97–138"). Tek bölümlü hatta bölüm elle aralıkla da yazılır.
 */
export function oemMetni(o: Pick<OemKaydi, "rn" | "baskaRiser" | "bolumler">, sayfaMetni: string): string {
  const p = [o.baskaRiser ? cevir("OEM MRB of {0}", o.rn) : cevir("OEM MRB")];
  if (sayfaMetni && o.bolumler.length) p.push(`${cevir("section")} ${o.bolumler.join(" / ")}`);
  if (sayfaMetni) p.push(sayfaMetni);
  return p.join(" · ");
}

/**
 * D-068: "Fitted (assembly)" hücresinin metni. Değer Assembly sayfasındaki Continuity kutusuyla aynı karşılaştırmadan gelir (sunucu
 * hesaplar, saklanmaz). `takilan` null → "—" (okunmuş montaj kaydı yok ya da yuvada birim yok) · kimlik okunduysa temiz kimlik ·
 * yalnız yazım varsa yazım (gri) ve "no serial number read" çipi · ikisi de yoksa "not read". Rol formda yazmıyor ve iki C&K hattı
 * yazılış sırasıyla gösteriliyorsa (`formSirasi`) hücre formun iki numarasını birlikte yazar: "{sn 1} · {sn 2}".
 */
export interface TakilanHucresi {
  metin: string;
  /** "kimlik": eş aralıklı · "yazim": eş aralıklı ve gri · "okunmadi": gri · "tire": düz */
  tur: "tire" | "kimlik" | "yazim" | "okunmadi";
  cip: Cip | null;
}
export function takilanHucresi(t: Pick<IzlenebilirlikTakilan, "sn" | "yazim" | "formSirasi"> | null): TakilanHucresi {
  if (!t) return { metin: "—", tur: "tire", cip: null };
  if (t.formSirasi?.length) return { metin: t.formSirasi.join(" · "), tur: "kimlik", cip: null };
  if (t.sn) return { metin: t.sn, tur: "kimlik", cip: null };
  if (t.yazim) return { metin: t.yazim, tur: "yazim", cip: { ton: "warn", metin: cevir("no serial number read") } };
  return { metin: cevir("not read"), tur: "okunmadi", cip: null };
}

/**
 * D-068: "Fitted (assembly)" hücresinin çipleri, bu sırayla: (1) beyan: "from riser R142" · "same part" · "new from stock · PKG-0007" ya
 * da "new from stock" · "source unknown"; (2) beyan yokken farklı çıkan hat kırmızı: dayanak OEM ise "differs from the OEM record, not
 * declared", değilse "differs from removed, not declared"; (3) yuva değişimi: tek hat öteki yuvadaysa "same line, now in the other
 * slot", iki hat karşılıklı yer değiştirdiyse "Choke and Kill swapped"; (4) "uncertain reading"; (5) Choke ve Kill'de rol formda
 * yazmıyorsa (yuva söküm kaydına, öteki hatta, yazılış sırasına ya da ekrandaki seçime göre) gri "role not written on the form";
 * (6) "removed after assembly".
 */
export function takilanCipleri(t: IzlenebilirlikTakilan | null): Cip[] {
  if (!t) return [];
  const c: Cip[] = [];
  const b = t.bag;
  if (b?.tur === "baska_riser") c.push({ ton: "warn", metin: cevir("from riser {0}", b.rn ?? "—") });
  else if (b?.tur === "ayni") c.push({ ton: "ok", metin: cevir("same part") });
  else if (b?.tur === "stok") c.push({ ton: "info", metin: b.paketNo != null ? cevir("new from stock · {0}", pkgNo(b.paketNo)) : cevir("new from stock") });
  else if (b?.tur === "bilinmiyor") c.push({ ton: "neutral", metin: cevir("source unknown") });
  if (!b && t.sonuc === "farkli") c.push({ ton: "bad", metin: t.dayanak === "oem" ? cevir("differs from the OEM record, not declared") : cevir("differs from removed, not declared") });
  if (t.sonuc === "rol_farki") c.push({ ton: "warn", metin: t.rolFarki === "oteki_yuva" ? cevir("same line, now in the other slot") : cevir("Choke and Kill swapped") });
  if (t.sonuc === "belirsiz" || t.okumaBelirsiz) c.push({ ton: "warn", metin: cevir("uncertain reading") });
  if (t.rolKaynagi && t.rolKaynagi !== "kayit") c.push({ ton: "neutral", metin: cevir("role not written on the form") });
  if (t.sokuldu) c.push({ ton: "neutral", metin: cevir("removed after assembly") });
  return c;
}

/**
 * D-068: verici riserın "Line SN" hücresinin altındaki not: bu riserdan sökülen hat başka riserın montaj kaydında okunuyor (hesaplanan).
 * O montaj bu sökümden önce tarihliyse kırmızı "also on the assembly record of R118, dated before this disassembly". Değilse not alıcı
 * riserdaki beyana bağlıdır: beyan varsa "now fitted in R140 (declared)" (sarı); yoksa yalnız bilgi "also read on the assembly record
 * of R140, not declared" (gri): tek hane yanlış okunmuş bir numara gerçek geçiş gibi yazılmaz.
 */
export function simdiTakiliCipi(s: SimdiTakili): Cip {
  if (s.zaman === "once") return { ton: "bad", metin: cevir("also on the assembly record of {0}, dated before this disassembly", s.rn) };
  return s.beyanli ? { ton: "warn", metin: cevir("now fitted in {0} (declared)", s.rn) } : { ton: "neutral", metin: cevir("also read on the assembly record of {0}, not declared", s.rn) };
}

/* ---------------------------------------------------------------- "Recertification document list" ---------------------------------------------------------------- */

/** Listenin bölüm şeridi; Bölüm 4'te blokları da yazar: "4 · Component Traceability and Manufacturing — 4.1 Main Tube · 4.2 Choke". */
export function listeBolumBasligi(b: Pick<DosyaBolumu, "no" | "ad" | "bloklar">): string {
  const bloklar = b.bloklar.map((x) => `4.${x.sira} ${hatAdi(x.hat)}`).join(" · ");
  return bloklar ? `${b.no} · ${b.ad} — ${bloklar}` : `${b.no} · ${b.ad}`;
}

/**
 * Listedeki bağlantının metni: tür adı ve satırı ayıran kısa ek (hat, kütük kodu, OEM kitabı sayfası, iş emri).
 * Beyanda beyanın adı ve yeri: "No measurement record available: Main Tube W1". D-072: beyan edilen şey yerin parçasıdır, ham
 * ("No record available: Fastener #48 Bolt (Locking Bolt) ×6").
 */
export function listeBelgeAdi(s: DosyaSatiri, b: Bicim, isEmri?: string | null): string {
  if (s.kayit === "beyan") {
    const nerede = [s.adimAd ? cevir(s.adimAd) : "", s.beyanKonusu ?? "", yer(s.hat, s.kaynakNolar)].filter(Boolean).join(", ");
    return nerede ? `${beyanAdlari(s).join(", ")}: ${nerede}` : beyanAdlari(s).join(", ");
  }
  const ek: string[] = [];
  if (s.kutuk) ek.push(kutukKodu(s.kutuk));
  else if (s.kayit === "fitup") ek.push(hatAdi(s.hat));
  else if (s.kapsam === "hat") ek.push(hatAdi(s.hat));
  else if (s.kapsam === "is" || s.kapsam === "kaynak") ek.push(yer(s.hat, s.kaynakNolar));
  else if (s.kapsam === "is_emri" && isEmri) ek.push(`${cevir("work order")} ${isEmri}`);
  if (mrbSayfasiMi(s) && s.sayfa) ek.push(`${cevir("OEM MRB")} ${b.sayfa(s.sayfa)}`);
  const dolu = ek.filter(Boolean);
  return dolu.length ? `${satirAdi(s)} (${dolu.join(" · ")})` : satirAdi(s);
}

/**
 * Yuvayı aynı türün öteki yuvalarından ayıran ek: hat · blok ve kaynak ("4.1 Main Tube W2") · kütük kodu · ilgili rapor.
 * D-062: 5.4'te yuvanın `kod`'u raporu imzalayan NDT inspektörünün sistem kimliğidir ve hiçbir zaman yazılmaz; yerine yuvadaki ad
 * yazılır: "technician Deniz Örnek · NDT report Kill". Ad gelmediyse yalnız "technician"; imzalayanı girilmemiş raporun yuvasında
 * (kod yok) yalnız rapor adı.
 */
function yuvaEki(y: Yuva, d: Dizin): string {
  const ek: string[] = [];
  if (y.hat) ek.push(hatAdi(y.hat));
  if (y.isId) {
    const blok = d.blok.get(y.isId);
    ek.push(blok ? `4.${blok.sira} ${hatAdi(blok.hat)}` : isAdi(y.isId));
  }
  if (y.kaynakNo !== undefined) ek.push(`W${y.kaynakNo}`);
  const imzalayanYuvasi = y.tur === "ndt_yeterlilik";
  const personel = imzalayanYuvasi && y.kod ? teknisyen(y.ad) : null;
  if (y.kod && !imzalayanYuvasi) ek.push(y.kod);
  if (y.satirId) {
    const s = d.satir.get(y.satirId);
    if (s) ek.push([satirAdi(s), hatAdi(s.hat)].filter(Boolean).join(" "));
  }
  return [personel, ek.join(" ")].filter(Boolean).join(" · ");
}

/** Eksik yuvanın adı: "Pressure Test Certificate and chart (Kill)" · "Fit-up record (4.1 Main Tube W2)" · "WPS and PQR (W-00313)". */
export function yuvaAdi(y: Yuva, d: Dizin = BOS_DIZIN): string {
  const ek = yuvaEki(y, d);
  return ek ? `${turAdi(y.tur, y.rn)} (${ek})` : turAdi(y.tur, y.rn);
}

/** Eksik yuvaların adları; aynı türün yuvaları tek adda toplanır: "Pressure Test Certificate and chart (Kill, Booster, Hydraulic)". */
export function yuvaAdlari(yuvalar: readonly Yuva[], d: Dizin = BOS_DIZIN): string[] {
  const gruplar = new Map<string, string[]>();
  for (const y of yuvalar) {
    const ad = turAdi(y.tur, y.rn);
    const ekler = gruplar.get(ad) ?? [];
    const ek = yuvaEki(y, d);
    if (ek) ekler.push(ek);
    gruplar.set(ad, ekler);
  }
  return [...gruplar].map(([ad, ekler]) => (ekler.length ? `${ad} (${ekler.join(", ")})` : ad));
}

/** "Not applicable"ın nedeni; nedeni yazılmayan kalemde (3.8, 4.6, 5.6) tire. */
export function dogmazMetni(n: DogmazNedeni | null): string {
  switch (n) {
    case "kaynak_onarimi_yok": return cevir("no weld repair on this riser");
    case "malzeme_gerekmiyor": return cevir("no new material in the repair jobs");
    case "adim_yok": return cevir("not in the route of the repair jobs");
    case "istenmiyor": return cevir("not required (stated on the Pressure Test Certificate)");
    default: return "—";
  }
}

/**
 * Hiç belgesi olmayan kalemin "Documents" hücresi, onaylı listedeki metinle: 1.1 ve 2.2'de sonda düzenlenecek belge, 1.5'te beklenen
 * fotoğraflar; 3.1 ve 5.4'te tire (beklenen belgenin adı yazılmaz; 5.4'te kaydı olmayan imzalayan varsa `listeParcalari` onu yazar).
 * Öteki kalemlerde null: hücreyi `listeParcalari` kurar.
 */
function belgesizKalemMetni(no: KalemNo): string | null {
  switch (no) {
    case "1.1": return cevir("Certificate of Conformity for this riser");
    case "1.5": return cevir("Identification plate photos");
    case "2.2": return turAdi("dnv_survey_report");
    case "3.1":
    case "5.4": return "—";
    default: return null;
  }
}

/** "Documents" hücresinin bir parçası: `satir` doluysa o satırın bağlantısı, değilse düz metin. */
export interface ListeParcasi {
  metin: string;
  satir: DosyaSatiri | null;
}

/**
 * Liste satırının "Documents" hücresi. Kalemin belgeleri ve beyanları (her biri kendi bağlantısıyla), başka kaleme atıf ("covered by
 * 3.6"; 3.9'da FM-0012 bağlantısı) ve boş kalan yuvalar ("missing: …"). Hiç belge yoksa: onaylı listede metni olan kalemde o metin
 * (`belgesizKalemMetni`), ötekilerde beklenen belgenin adı, "Not applicable"ın nedeni ya da tire. Beklenen belgenin adı kalemin
 * adıyla aynıysa yinelenmez.
 */
export function listeParcalari(k: ListeKalemi, d: Dizin, b: Bicim, isEmri?: string | null): ListeParcasi[] {
  const p: ListeParcasi[] = [];
  // D-068: okunmuş montaj kaydında ölçü sayfası (basılı 3. sayfa) taranmamışsa "confirmed" denmez; öteki her durumda eski cümle.
  if (k.no === "3.9") p.push(...k.satirlar.map((s) => ({ satir: s, metin: s.olcuSayfasiEksik ? cevir("FM-0012 is on file, but its measurement page (page 3) is not in the scan") : cevir("pin stick-out confirmed on FM-0012") })));
  else p.push(...k.satirlar.map((s) => ({ satir: s, metin: listeBelgeAdi(s, b, isEmri) })));
  p.push(...k.beyanlar.map((s) => ({ satir: s, metin: listeBelgeAdi(s, b, isEmri) })));
  if (k.no === "4.8" && k.atif && k.durum !== "dogmaz") p.push({ satir: null, metin: cevir("covered by {0}", k.atif) });
  if (!p.length) {
    // D-062: 5.4'te sertifikası yüklenmemiş imzalayan adıyla yazılır (yuvada `kod` = inspektörün kimliği, `ad` = adı); imzalayanı
    // girilmemiş rapor için tire kalır.
    const imzalayanlar = k.no === "5.4" ? yuvaAdlari(k.eksik.filter((y) => y.kod), d) : [];
    if (imzalayanlar.length) return [{ satir: null, metin: imzalayanlar.join("; ") }];
    const belgesiz = belgesizKalemMetni(k.no);
    if (belgesiz !== null) return [{ satir: null, metin: belgesiz }];
  }
  if (k.eksik.length) {
    const onceVar = p.length > 0;
    const adlar = yuvaAdlari(k.eksik.filter((y) => onceVar || DOSYA_TUR_ADI[y.tur] !== k.ad), d);
    if (adlar.length) p.push({ satir: null, metin: onceVar ? `${cevir("missing")}: ${adlar.join("; ")}` : adlar.join("; ") });
  }
  if (p.length) return p;
  return [{ satir: null, metin: k.durum === "dogmaz" ? dogmazMetni(k.dogmazNedeni) : "—" }];
}
